import { Fetch } from "../HttpClient/FetchImplementation/Fetch";
import { ExceptionLogger, HttpClient } from "../HttpClient/HttpClient";
import { IHttpClient } from "../HttpClient/IHttpClient";
import { AuthenticationMode, createAuthenticationProvider } from "../HttpClient/AuthenticationMode";
import { ICandyApiDraftClient } from "../CandyApiClient/ICandyApiClient";
import { delay } from "../../Engine/src/Engine/utils";
import { isNullOrEmpty, reject } from "../TypingUtils";
import { DraftPresentationType } from "../CandyApiClient/Types/Presentations/DraftPresentationType";
import { waitPollingTask } from "../CandyApiClient/Types/PollingTasks/PollingTaskWaiter";

import {
    FSPrinterTaskResult,
    FSPrinterTaskStatus,
    IFSPrinterClient,
    PrintByRulesOptions,
    PrinterOptions,
    PrinterRequestOptions,
    PrinterResultTypes,
    RSVPrinterOptions,
} from "./IFSPrinterClient";

export class FSPrinterClient implements IFSPrinterClient {
    private readonly httpClient: IHttpClient;
    private readonly candyApiDraftClient: ICandyApiDraftClient;

    private pullingDelay: number = 0;
    private factor: number = 0;
    private timeout: number = 0;

    private readonly defaultPullingDelay: number = 1200;
    private readonly defaultFactor: number = 2;
    private readonly defaultTimeout: number = 1000 * 60 * 10;

    private taskResult: FSPrinterTaskResult | undefined;

    public constructor(
        fetchImpl: Fetch,
        baseUrl: string,
        resourceZone: undefined | string,
        authentication: undefined | AuthenticationMode,
        candyApiDraftClient: ICandyApiDraftClient,
        exceptionLogger?: ExceptionLogger
    ) {
        this.httpClient = new HttpClient(
            fetchImpl,
            baseUrl,
            createAuthenticationProvider(authentication),
            {
                resourceZone: resourceZone,
            },
            exceptionLogger
        );
        this.candyApiDraftClient = candyApiDraftClient;
    }

    public async printRSV(
        {
            type,
            resultType = PrinterResultTypes.Excel,
            presentation = DraftPresentationType.fuf,
            innerFuf,
        }: RSVPrinterOptions,
        requestOptions?: PrinterRequestOptions
    ): Promise<void> {
        const options: PrinterOptions = {
            resultType: resultType,
            rsvInfo: { type: type },
            presentation: presentation,
        };
        if (innerFuf?.driveUri) {
            options.fuf = { driveUri: innerFuf?.driveUri };
        }
        await this.print(options, requestOptions);
    }

    public async printByRules(options: PrintByRulesOptions): Promise<void> {
        const partialFufUri = await this.getPartialFufDriveUrl(options.rules);
        await this.print({
            fuf: {
                driveUri: partialFufUri,
            },
            resultType: PrinterResultTypes.Pdf,
            ...options.options,
        });
    }

    public async print(options: PrinterOptions, requestOptions?: PrinterRequestOptions): Promise<void> {
        this.applyRequestOptions(requestOptions);
        const task = await this.createTask(options);
        await this.runMonitoringTask(task.taskId);
        const resultUrl = this.taskResult?.resultUri;
        this.resetPrintResult();
        this.downloadDocument(resultUrl);
    }

    private async getPartialFufDriveUrl(rules: string[]): Promise<string> {
        const downloadFufHref = await this.candyApiDraftClient.getPartialFuf(rules);
        if (downloadFufHref && downloadFufHref?.ContentUri) {
            return downloadFufHref?.ContentUri;
        }
        throw new Error("downloadFufHref is not defined");
    }

    private async getInnerFufDriveUrl(): Promise<string> {
        await this.candyApiDraftClient.startGetInnerFufTask();
        const downloadHref = await waitPollingTask(() => this.candyApiDraftClient.getInnerFufTask());
        if (downloadHref) {
            return downloadHref;
        }
        throw new Error("downloadFufHref is not defined");
    }

    private async getFufDriveUrl(): Promise<string> {
        await this.candyApiDraftClient.startGetFufWithAttachmentsTask();
        const downloadFufHref = await waitPollingTask(() => this.candyApiDraftClient.getFufWithAttachmentsTask("Json"));
        if (downloadFufHref && "fuf" in downloadFufHref && downloadFufHref?.fuf.contentUri) {
            return downloadFufHref?.fuf.contentUri;
        }
        throw new Error("downloadFufHref is not defined");
    }

    private async runMonitoringTask(taskId: string): Promise<void> {
        while (!this.taskResult?.resultUri) {
            await this.checkTask(taskId);
            if (!this.taskResult?.resultUri) {
                await delay(this.pullingDelay);
                this.pullingDelay *= this.factor;
            }
        }
    }

    private async checkTask(taskId: string): Promise<void> {
        const taskStatus = await this.httpClient.get(`v3/tasks/${taskId}`).json<FSPrinterTaskResult>();
        if (taskStatus.errorInfo) {
            throw new Error(taskStatus.errorInfo.userMessage);
        }
        if (taskStatus.resultUri) {
            this.taskResult = taskStatus;
            return;
        }
        if (this.timeout <= this.pullingDelay) {
            throw new Error(`Timeout ${this.timeout / 60 / 1000} min for print file has expired`);
        }
    }

    private async createTask(options: PrinterOptions): Promise<FSPrinterTaskStatus> {
        const presentationUri = await this.getPresentationUri(options);

        const optionsWithFuf = this.appendFufUrlToOptions(options, presentationUri);
        const optionsWithSender = this.appendSenderAppToOptions(optionsWithFuf);
        const taskInfo = await this.httpClient
            .post("v3/tasks")
            .withJsonBody(optionsWithSender)
            .json<FSPrinterTaskStatus>();
        return taskInfo;
    }

    private async getPresentationUri(options: PrinterOptions) {
        if (options.presentation === DraftPresentationType.innerFuf) {
            if (options.fuf?.driveUri) {
                return options.fuf.driveUri;
            }
            return await this.getInnerFufDriveUrl();
        }
        return options.fuf?.driveUri ?? (await this.getFufDriveUrl());
    }

    private downloadDocument(driveUrl: string | undefined) {
        if (isNullOrEmpty(driveUrl)) {
            reject(`Ссылка на печать документа пустая или undefined. Трейс печати: ${this.taskResult?.taskId}`);
        }
        window.location.href = driveUrl;
    }

    private appendFufUrlToOptions(options: PrinterOptions, fufUrl: string): PrinterOptions {
        return { ...options, fuf: { driveUri: fufUrl } };
    }

    private appendSenderAppToOptions(options: PrinterOptions): PrinterOptions {
        return { ...options, senderApp: "candy-front" };
    }

    private applyRequestOptions(requestOptions?: PrinterRequestOptions) {
        this.pullingDelay = requestOptions?.pullingDelay ?? this.defaultPullingDelay;
        this.factor = requestOptions?.factor ?? this.defaultFactor;
        this.timeout = requestOptions?.timeout ?? this.defaultTimeout;
    }

    private resetPrintResult() {
        this.taskResult = undefined;
    }
}
